import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

// ---- Auth ----
export async function apiRegister(email, password, name) {
  const { data } = await axios.post(`${API}/auth/register`, { email, password, name });
  return data;
}
export async function apiLogin(email, password) {
  const { data } = await axios.post(`${API}/auth/login`, { email, password });
  return data;
}
export async function apiLogout() {
  await axios.post(`${API}/auth/logout`, {});
}
export async function apiMe() {
  const { data } = await axios.get(`${API}/auth/me`);
  return data;
}
export async function apiGoogleSession(sessionId) {
  const { data } = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { headers: { "X-Session-ID": sessionId } });
  return data;
}
export async function apiForgotPassword(email) {
  const { data } = await axios.post(`${API}/auth/forgot-password`, { email, origin: window.location.origin });
  return data;
}
export async function apiResetPassword(token, password) {
  const { data } = await axios.post(`${API}/auth/reset-password`, { token, password });
  return data;
}

// ---- fal key (per-user, server-side) ----
export async function getFalKeyStatus() {
  const { data } = await axios.get(`${API}/user/fal-key`);
  return data;
}
export async function saveFalKey(key) {
  const { data } = await axios.post(`${API}/user/fal-key`, { key });
  return data;
}
export async function deleteFalKey() {
  const { data } = await axios.delete(`${API}/user/fal-key`);
  return data;
}
export async function getCredits() {
  const { data } = await axios.get(`${API}/user/credits`);
  return data;
}
export async function getSpend() {
  const { data } = await axios.get(`${API}/user/spend`);
  return data;
}

// ---- Connections (multi-provider) ----
export async function getConnections() {
  const { data } = await axios.get(`${API}/user/connections`);
  return data;
}
export async function getBalances() {
  const { data } = await axios.get(`${API}/user/balances`);
  return data;
}
export function mediaDownloadUrl(url, filename) {
  const p = new URLSearchParams({ url });
  if (filename) p.set("filename", filename);
  return `${API}/media/download?${p.toString()}`;
}
export async function discoverMusicModels() {
  const { data } = await axios.post(`${API}/music/discover`);
  return data;
}
export async function setConnection(provider, key) {
  const { data } = await axios.post(`${API}/user/connections/${provider}`, { key });
  return data;
}
export async function removeConnection(provider) {
  const { data } = await axios.delete(`${API}/user/connections/${provider}`);
  return data;
}

// ---- Voice (ElevenLabs) ----
export async function listVoices() {
  const { data } = await axios.get(`${API}/voice/voices`);
  return data;
}
export async function voiceQuota() {
  const { data } = await axios.get(`${API}/voice/quota`);
  return data;
}
export async function tts(payload) {
  const { data } = await axios.post(`${API}/voice/tts`, payload, { timeout: 180000 });
  return data;
}
export async function sfx(payload) {
  const { data } = await axios.post(`${API}/voice/sfx`, payload, { timeout: 180000 });
  return data;
}
export async function cloneVoice(name, description, files) {
  const form = new FormData();
  form.append("name", name);
  form.append("description", description || "");
  files.forEach((f) => form.append("files", f));
  const { data } = await axios.post(`${API}/voice/clone`, form, {
    headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
  });
  return data;
}
export async function voiceHistory() {
  const { data } = await axios.get(`${API}/voice/history`);
  return data;
}
export async function deleteVoice(id) {
  await axios.delete(`${API}/voice/history/${id}`);
}

// ---- Music (fal) ----
export async function generateMusic(payload) {
  const { data } = await axios.post(`${API}/music`, payload, { timeout: 300000 });
  return data;
}
export async function musicHistory() {
  const { data } = await axios.get(`${API}/music/history`);
  return data;
}
export async function deleteMusic(id) {
  await axios.delete(`${API}/music/history/${id}`);
}

// ---- Studio ----
export async function fetchConfig() {
  const { data } = await axios.get(`${API}/config`);
  return data;
}
export async function uploadAsset(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(`${API}/upload`, form, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}
export async function generate(payload) {
  const { data } = await axios.post(`${API}/generate`, payload, { timeout: 300000 });
  return data;
}
export async function enhancePrompt(model_id, prompt, editing) {
  const { data } = await axios.post(`${API}/enhance-prompt`, { model_id, prompt, editing });
  return data.enhanced;
}
export async function fetchHistory(type, q, extra = {}) {
  const params = {};
  if (type && type !== "all") params.type = type;
  if (q) params.q = q;
  if (extra.favorite) params.favorite = true;
  if (extra.tag) params.tag = extra.tag;
  const { data } = await axios.get(`${API}/history`, { params });
  return data;
}
export async function updateGeneration(id, patch) {
  const { data } = await axios.patch(`${API}/history/${id}`, patch);
  return data;
}
export async function fetchTags() {
  const { data } = await axios.get(`${API}/history/tags`);
  return data.tags || [];
}
export async function exportHistory(ids) {
  const resp = await axios.post(`${API}/history/export`, { ids }, { responseType: "blob" });
  return resp.data;
}
export async function deleteGeneration(id) {
  await axios.delete(`${API}/history/${id}`);
}
export async function cropAsset(url, box) {
  const { data } = await axios.post(`${API}/assets/crop`, { url, ...box });
  return data;
}
export async function inpaintImage(image_url, mask_url, prompt) {
  const { data } = await axios.post(`${API}/inpaint`, { image_url, mask_url, prompt });
  return data;
}

export async function upscaleImage(image_url, source_id, factor = 2) {
  const { data } = await axios.post(`${API}/upscale`, { image_url, source_id, factor }, { timeout: 300000 });
  return data;
}

export async function extendImage(image_url, source_id, opts = {}) {
  const { data } = await axios.post(`${API}/extend`, { image_url, source_id, ...opts }, { timeout: 300000 });
  return data;
}

export async function generateStream(payload, onEvent) {
  const startedAt = Date.now();
  const resp = await fetch(`${API}/generate/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (!resp.ok || !resp.body) {
    let detail = "Generation failed";
    try { const j = await resp.json(); detail = j.detail || detail; } catch (e) { console.error("Failed to parse error response", e); }
    const err = new Error(detail); err.status = resp.status; throw err;
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let final = null;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf("\n\n")) >= 0) {
      const chunk = buf.slice(0, idx); buf = buf.slice(idx + 2);
      const line = chunk.split("\n").find((l) => l.startsWith("data:"));
      if (!line) continue;
      let data;
      try { data = JSON.parse(line.slice(5).trim()); } catch (e) { continue; }
      if (data.type === "complete") final = data.record;
      else if (data.type === "error") { const e = new Error(data.detail); e.status = data.status || 500; throw e; }
      else if (onEvent) onEvent(data);
    }
  }
  if (!final) {
    // The connection was cut before a result (proxy caps long video renders),
    // but the job keeps running server-side. Recover by polling history.
    final = await recoverGeneration(startedAt, payload, onEvent);
  }
  return final;
}

async function recoverGeneration(startedAt, payload, onEvent) {
  if (onEvent) onEvent({ type: "in_progress", message: "still rendering — finishing up…" });
  const floor = startedAt - 5000;
  for (let i = 0; i < 72; i++) {
    await new Promise((r) => setTimeout(r, 5000));
    try {
      const { data } = await axios.get(`${API}/history`, { params: {} });
      const list = data || [];
      const match = list.find((r) =>
        r.outputs?.length &&
        new Date(r.created_at).getTime() >= floor &&
        (r.prompt || "") === (payload.prompt || ""));
      if (match) return match;
    } catch (e) { console.error("recover poll failed", e); }
  }
  throw new Error("Stream ended without a result");
}

export function computeCost(model, params) {
  if (!model) return 0;
  const p = model.pricing;
  if (model.type === "image") {
    const n = params.num_images || 1;
    if (p.mode === "per_image") return p.amount * n;
    if (p.mode === "per_mp") {
      const mp = ((params.width || 1024) * (params.height || 1024)) / 1_000_000;
      return p.amount * mp * n;
    }
  } else {
    if (p.mode === "per_second") return p.amount * (params.duration || 5);
    if (p.mode === "per_video") return p.amount;
  }
  return 0;
}
